import {
  LOADING_TOGGLE_ACTION,
  LOGIN_CONFIRMED_ACTION,
  LOGIN_FAILED_ACTION,
  LOGOUT_ACTION,
  SIGNUP_CONFIRMED_ACTION,
  SIGNUP_FAILED_ACTION,
  COMPANY_SELECTED_ACTION,
  CREATE_NEW_COMPANY_ACTION,
  SET_ACCESS_TOKEN,
  SET_USER_DATA,
  SELECT_COMPANY,
  PASSWORD_RESET_REQUEST,
  PASSWORD_RESET_SUCCESS,
  PASSWORD_RESET_FAILED,
  // Enhanced action types for account linking
  GOOGLE_ONLY_USER_DETECTED,
  ACCOUNT_METHODS_CHECKED,
  TRIGGER_GOOGLE_SIGNIN,
  CLEAR_AUTH_MESSAGES,
} from '../actions/AuthActions';

const initialState = {
  selectedCompany: {
    companyName: '',
    companyId: '',
    companyRegNo: '',
  },
  user: {
    googleUid: '',
    uid: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    companies: {},
  },
  accessToken: null,
  isAuthenticated: false,
  selectedCompanyId: '',
  isCompanySelected: false,
  errorMessage: '',
  successMessage: '',
  showLoading: false,

  // Enhanced state for account linking
  accountInfo: {
    email: '',
    hasPassword: false,
    hasGoogle: false,
    availableMethods: [],
    isGoogleOnly: false,
    canAddPassword: false,
    shouldTriggerGoogle: false,
  },

  // Password reset state
  passwordReset: {
    isRequested: false,
    isSuccess: false,
    isFailed: false,
    email: '',
    code: '',
    availableMethods: [],
    canAddPassword: false,
  },

  // UI state
  ui: {
    showGoogleOnlyPrompt: false,
    lastCheckedEmail: '',
    retryCount: 0,
    isInitialized: false,
  }
};

export function AuthReducer(state = initialState, action) {
  switch (action.type) {
    case SET_ACCESS_TOKEN:
      return {
        ...state,
        accessToken: action.payload,
        isAuthenticated: true,
        errorMessage: '',
        ui: {
          ...state.ui,
          isInitialized: true
        }
      };

    case SET_USER_DATA:
      return {
        ...state,
        user: {
          ...state.user,
          uid: action.payload.user.user_id,
          firstName: action.payload.user.first_name,
          lastName: action.payload.user.last_name,
          phoneNumber: action.payload.user.phone_number,
          email: action.payload.user.email || state.user.email,
          googleUid: action.payload.user.google_id,
          // Keep companies as object for easy access
          companies: action.payload.companies || {},
        },
        isAuthenticated: true,
        ui: {
          ...state.ui,
          isInitialized: true
        }
      };

    case SIGNUP_CONFIRMED_ACTION:
      return {
        ...state,
        user: {
          ...state.user,
          googleUid: action.payload.data.googleUid,
          email: action.payload.data.email,
          firstName: action.payload.data.firstName,
          lastName: action.payload.data.lastName,
          uid: action.payload.data.uid,
        },
        isAuthenticated: true,
        errorMessage: '',
        successMessage: 'Registrering genomförd framgångsrikt',
        showLoading: false,
        // Clear account info on successful signup
        accountInfo: {
          ...initialState.accountInfo,
          email: action.payload.data.email
        }
      };

    case LOGIN_CONFIRMED_ACTION:
      return {
        ...state,
        user: {
          ...state.user,
          googleUid: action.payload.data.googleUid,
          email: action.payload.data.email,
          firstName: action.payload.data.firstName,
          lastName: action.payload.data.lastName,
          uid: action.payload.data.uid,
        },
        errorMessage: '',
        successMessage: 'Inloggning genomförd framgångsrikt',
        showLoading: false,
        isAuthenticated: true,
        // Clear account info and UI state on successful login
        accountInfo: {
          ...initialState.accountInfo,
          email: action.payload.data.email
        },
        ui: {
          ...state.ui,
          showGoogleOnlyPrompt: false,
          isInitialized: true
        }
      };

    case LOGOUT_ACTION:
      return {
        ...initialState,
        isAuthenticated: false,
        ui: {
          ...initialState.ui,
          isInitialized: true
        }
      };

    case SIGNUP_FAILED_ACTION:
    case LOGIN_FAILED_ACTION:
      return {
        ...state,
        errorMessage: action.payload,
        successMessage: '',
        showLoading: false,
        ui: {
          ...state.ui,
          retryCount: state.ui.retryCount + 1
        }
      };

    case LOADING_TOGGLE_ACTION:
      return {
        ...state,
        showLoading: action.payload,
      };

    case COMPANY_SELECTED_ACTION:
      return {
        ...state,
        selectedCompany: {
          companyName: action.payload.companyName,
          companyId: action.payload.companyId,
          companyRegNo: action.payload.companyRegNumber,
        },
        selectedCompanyId: action.payload.companyId,
        isCompanySelected: true,
        errorMessage: '',
        successMessage: '',
      };

    case SELECT_COMPANY:
      return {
        ...state,
        selectedCompany: {
          companyName: action.payload.company.companyName,
          companyId: action.payload.companyId,
          companyRegNo: action.payload.company.companyRegNo || '',
        },
        selectedCompanyId: action.payload.companyId,
        isCompanySelected: true,
        errorMessage: '',
        successMessage: '',
      };

    case CREATE_NEW_COMPANY_ACTION:
      return {
        ...state,
        selectedCompany: {
          companyName: action.payload?.companyName || '',
          companyId: action.payload?.companyId || '',
          companyRegNo: action.payload?.companyRegNo || '',
        },
        isCompanySelected: true,
        errorMessage: '',
      };

    // Enhanced Password Reset Actions
    case PASSWORD_RESET_REQUEST:
      return {
        ...state,
        passwordReset: {
          ...state.passwordReset,
          isRequested: true,
          isSuccess: false,
          isFailed: false,
          email: action.payload?.email || '',
        },
        showLoading: true,
        errorMessage: '',
        successMessage: '',
      };

    case PASSWORD_RESET_SUCCESS:
      return {
        ...state,
        passwordReset: {
          ...state.passwordReset,
          isRequested: false,
          isSuccess: true,
          isFailed: false,
          email: action.payload?.email || state.passwordReset.email,
        },
        errorMessage: '',
        successMessage: action.payload.message,
        showLoading: false,
      };

    case PASSWORD_RESET_FAILED:
      return {
        ...state,
        passwordReset: {
          ...state.passwordReset,
          isRequested: false,
          isSuccess: false,
          isFailed: true,
          availableMethods: action.payload?.availableMethods || [],
          canAddPassword: action.payload?.canAddPassword || false,
        },
        errorMessage: action.payload.message,
        successMessage: '',
        showLoading: false,
      };

    // Enhanced Account Linking Actions
    case GOOGLE_ONLY_USER_DETECTED:
      return {
        ...state,
        accountInfo: {
          ...state.accountInfo,
          email: action.payload.email,
          hasPassword: false,
          hasGoogle: true,
          isGoogleOnly: true,
          canAddPassword: action.payload.canAddPassword || false,
          shouldTriggerGoogle: action.payload.canTriggerGoogle || false,
        },
        ui: {
          ...state.ui,
          showGoogleOnlyPrompt: true,
          lastCheckedEmail: action.payload.email,
        },
        errorMessage: action.payload.message || '',
        showLoading: false,
      };

    case ACCOUNT_METHODS_CHECKED:
      return {
        ...state,
        accountInfo: {
          ...state.accountInfo,
          email: action.payload.email,
          hasPassword: action.payload.hasPassword,
          hasGoogle: action.payload.hasGoogle,
          availableMethods: action.payload.methods || [],
          isGoogleOnly: action.payload.hasGoogle && !action.payload.hasPassword,
        },
        ui: {
          ...state.ui,
          lastCheckedEmail: action.payload.email,
        },
      };

    case TRIGGER_GOOGLE_SIGNIN:
      return {
        ...state,
        accountInfo: {
          ...state.accountInfo,
          shouldTriggerGoogle: action.payload.shouldTriggerGoogle,
        },
        ui: {
          ...state.ui,
          showGoogleOnlyPrompt: false,
        },
        showLoading: true,
      };

    case CLEAR_AUTH_MESSAGES:
      return {
        ...state,
        errorMessage: '',
        successMessage: '',
        accountInfo: {
          ...initialState.accountInfo,
          email: state.accountInfo.email, // Keep the email
        },
        passwordReset: {
          ...initialState.passwordReset,
          email: state.passwordReset.email, // Keep the email
        },
        ui: {
          ...state.ui,
          showGoogleOnlyPrompt: false,
        },
      };

    // Handle network errors and retries
    case 'AUTH_NETWORK_ERROR':
      return {
        ...state,
        errorMessage: 'Nätverksfel. Kontrollera din internetanslutning och försök igen.',
        showLoading: false,
        ui: {
          ...state.ui,
          retryCount: state.ui.retryCount + 1
        }
      };

    case 'AUTH_RETRY_RESET':
      return {
        ...state,
        ui: {
          ...state.ui,
          retryCount: 0
        }
      };

    // Handle initialization completion
    case 'AUTH_INITIALIZED':
      return {
        ...state,
        ui: {
          ...state.ui,
          isInitialized: true
        }
      };

    default:
      return state;
  }
}

// Enhanced selectors for the new state structure
export const selectAuthState = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsCompanySelected = (state) => state.auth.isCompanySelected;
export const selectSelectedCompany = (state) => state.auth.selectedCompany;
export const selectAccountInfo = (state) => state.auth.accountInfo;
export const selectPasswordResetState = (state) => state.auth.passwordReset;
export const selectUIState = (state) => state.auth.ui;
export const selectIsLoading = (state) => state.auth.showLoading;
export const selectErrorMessage = (state) => state.auth.errorMessage;
export const selectSuccessMessage = (state) => state.auth.successMessage;

// Enhanced compound selectors
export const selectIsGoogleOnlyUser = (state) =>
  state.auth.accountInfo.isGoogleOnly;

export const selectShouldShowGooglePrompt = (state) =>
  state.auth.ui.showGoogleOnlyPrompt;

export const selectCanTriggerGoogleSignIn = (state) =>
  state.auth.accountInfo.shouldTriggerGoogle;

export const selectHasAuthError = (state) =>
  Boolean(state.auth.errorMessage);

export const selectAuthRetryCount = (state) =>
  state.auth.ui.retryCount;

export const selectIsAuthInitialized = (state) =>
  state.auth.ui.isInitialized;

export const selectUserEmail = (state) =>
  state.auth.user.email || state.auth.accountInfo.email;

export const selectAvailableAuthMethods = (state) =>
  state.auth.accountInfo.availableMethods;
